<template>
    <div ref="rootElement">
        <div class="expertise-section__predictor">
            <div class="expertise-section__predictor-title">
                {{ t('profile.creation.predictor.wizard.details.manual.expertise.title') }}
            </div>
            <div class="expertise-section__predictor-subtitle">
                {{ t('profile.creation.predictor.wizard.details.manual.expertise.subtitle') }}
            </div>

            <VJoySelect
                class="expertise-section__predictor-manual-family-select expertise-section__manual-select"
                data-testid="freelancer-categorization-manual-selection-family-select"
                @update:model-value="manualFamilySelected"
            >
                <option disabled :selected="!hasFamilySelected" value="">
                    {{ t('profile.creation.predictor.wizard.details.manual.expertise.family.selection') }}
                </option>
                <option
                    v-for="family in families"
                    :key="family.familyId"
                    :value="family.familyId"
                    :selected="isFamilySelected(family)"
                    :data-testid="`freelancer-categorization-manual-selection-family-option--${family.familyId}`"
                >
                    {{ family.label }}
                </option>
            </VJoySelect>

            <div v-if="hasFamilySelected && !hasChoseOtherFamily" class="expertise-section__predictor-manual-category">
                <div class="expertise-section__predictor-manual-category-title">
                    {{ t('profile.creation.predictor.wizard.details.manual.expertise.family.subtitle') }}
                </div>

                <CategorizationSelectionList
                    data-testid="freelancer-categorization-category"
                    :categories="categorizationSelection"
                    :selected-categories="state.selectedCategories"
                    @categorization-selection:update="handleCategorizationSelectionUpdate"
                />
            </div>

            <VJoyWrapper v-if="hasChoseOtherFamily" item-stretch>
                <VJoyInput
                    v-model="otherCategoryModel"
                    data-testid="freelancer-categorization-manual-selection-input-other"
                    :placeholder="t('profile.creation.predictor.wizard.details.manual.expertise.category.placeholder')"
                    autocomplete="off"
                    name="other-category"
                    @update:model-value="otherCategoryChange"
                >
                    {{ t('profile.creation.predictor.wizard.details.manual.expertise.category.select') }}
                </VJoyInput>
            </VJoyWrapper>

            <VJoyWrapper v-if="hadPredictions" no-margin>
                <VJoyLink
                    href="#"
                    class="expertise-section__back"
                    data-testid="freelancer-categorization-predictions-switch-to-predictions"
                    :text="t('profile.creation.completion.wizard.categorization.category.back.help')"
                    @click="goBack"
                />
            </VJoyWrapper>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {computed, onMounted, reactive, ref, useTemplateRef} from 'vue';
    import {useTranslation} from '#imports';
    import {VJoyInput, VJoyLink, VJoySelect, VJoyWrapper} from '@maltjoy/core-vue';
    import type {ProfileFamilyResource} from '@api';
    import CategorizationSelectionList from './CategorizationSelectionList.vue';

    const emit = defineEmits<{
        'manual-selection': [];
        unselectCategories: [];
        goBack: [];
        inputFamily: [familyId: string];
        inputCategory: [selectedCategories: string[]];
        'selection-updated': [value: {familyId: string; categoryIds: string[]}];
        otherCategoryChange: [otherCategory: string];
    }>();

    const {t} = useTranslation();

    type Props = {
        families: ProfileFamilyResource[];
        categories: string[];
        hadPredictions: boolean;
    };

    const props = defineProps<Props>();

    const state = reactive<{
        selectedFamilyId: string;
        selectedCategories: string[];
    }>({
        selectedFamilyId: '',
        selectedCategories: props.categories,
    });

    function manualFamilySelected(familyId: string) {
        state.selectedCategories = [];
        emit('inputFamily', familyId);
        emit('otherCategoryChange', '');
        state.selectedFamilyId = familyId;
    }

    const otherCategoryModel = ref('');
    const rootElement = useTemplateRef('rootElement');
    const hasFamilySelected = computed(() => findSelectedFamily.value.length > 0);
    const hasChoseOtherFamily = computed(() => findSelectedFamily.value.map((family) => family.familyId).includes('other'));
    const linkedCategories = computed(() => findSelectedFamily.value.flatMap((family) => family.categories));
    const categorizationSelection = computed(() =>
        linkedCategories.value.map((category) => ({categoryId: category.categoryId, categoryLabel: category.label})),
    );
    const findSelectedFamily = computed(() => props.families.filter((family) => family.familyId === state.selectedFamilyId));

    onMounted(() => {
        if (state.selectedCategories.length === 0) {
            emit('manual-selection');
        }
    });

    // Handlers
    function handleCategorizationSelectionUpdate(newSelectedCategories?: string[]) {
        state.selectedCategories = newSelectedCategories || [];
        emit('inputCategory', state.selectedCategories);
        emit('selection-updated', {familyId: state.selectedFamilyId, categoryIds: state.selectedCategories});
    }

    function isFamilySelected(family: ProfileFamilyResource): boolean {
        return findSelectedFamily.value.includes(family);
    }

    function otherCategoryChange(category: string): void {
        emit('otherCategoryChange', category);
    }

    function unselectCategories(): void {
        emit('unselectCategories');
    }

    function goBack(): void {
        unselectCategories();
        emit('goBack');
    }
</script>

<style scoped lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .expertise-section {
        &__back {
            font-size: var(--joy-font-size-primary-300);
            margin-top: var(--joy-core-spacing-6);
        }
        &__predictor {
            padding: var(--joy-core-spacing-6);
            background-color: var(--joy-color-secondary-10);
            border-radius: var(--joy-core-radius-3);

            &-title {
                @include text.getFontBySize(primary-450);
                color: var(--joy-color-neutral-60);
                margin-bottom: var(--joy-core-spacing-2);
            }

            &-subtitle {
                @include text.getFontBySize(primary-300);
                color: var(--joy-color-neutral-60);
                margin-bottom: var(--joy-core-spacing-2);
            }

            joy-dropdown,
            select {
                margin-bottom: var(--joy-core-spacing-6);
            }

            &-manual-category {
                margin-top: var(--joy-core-spacing-6);

                &-title {
                    @include text.getFontBySize(primary-300);
                    color: var(--joy-color-neutral-60);
                    margin-bottom: var(--joy-core-spacing-2);
                }
            }
        }
    }
</style>
